import * as React from 'react';
import { AUTOMATION_ACTION_TYPE } from '../../../MedicareProAPI/Entities/Automation/AutomationAction';
import { USER_ACTIVITY_PRIORITY_TYPE_LABELS } from '../../Language/en';
import { WebAPI } from '../../../MedicareProAPI/API/WebAPI';
import type { SelectMap, SelectMapNumeric } from '../../Utilities/SelectMap';
import { StaffRepository } from '../../../MedicareProAPI/Repositories/Staff/StaffRepository';
import { UserActivityRepository } from '../../../MedicareProAPI/Repositories/User/UserActivityRepository';
import { UserLeadStatusRepository } from '../../../MedicareProAPI/Repositories/User/UserLeadStatusRepository';
import type { USER_ACTIVITY_PRIORITY_TYPE } from '../../../MedicareProAPI/Entities/User/UserActivity';
import { UserIntegrationProvider } from '../../../MedicareProAPI/Entities/User/UserIntegrationProvider';
import { App } from '../../../MedicareProAPI/Entities/App';
import { UserServiceSelect } from '../Form/Field/UserServiceSelect';
import type { ThanksIOTemplateProps } from '../Form/Field/ThanksIOTemplate';
import type { DirectMailManagerTemplateProps } from '../Form/Field/DirectMailManagerTemplate';
import type { INTEGRATION_TYPE } from '../../Constants/Integrations';
import { UserFlowRepository } from '../../../MedicareProAPI/Repositories/User/UserFlowRepository';
import type { UserFlow } from '../../../MedicareProAPI/Entities/User/UserFlow';
import { UserRepository } from '../../../MedicareProAPI/Repositories/User/UserRepository';
import type { UserFlowStage } from '../../../MedicareProAPI/Entities/User/UserFlowStage';

export interface AutomationActionActionDataProps {
  userStaffEnabled?: number;
  userStaff?: number[];
  userLeadStatusName?: string;
  userActivityCategory?: string;
  userActivityTitle?: string;
  userActivityPriority?: USER_ACTIVITY_PRIORITY_TYPE;
  userActivityDetails?: string;
  userVoIPExtensionId?: number;
  userVoIPPhoneNumberId?: number;
  userIntegrationProviderId?: number;
  userLetterTemplateId?: number;
  thanksioTemplate?: ThanksIOTemplateProps;
  directMailManagerTemplate?: DirectMailManagerTemplateProps;
  appId?: number;
  userContactSyncListId?: any;
  userContactSyncExtensionId?: number;
  userContactSyncAccountId?: number;
  userContactSyncGroupId?: any;
  userAudioFileId?: any;
  userFlow?: number;
  userFlowStage?: number;
}

export interface AutomationActionFormActionProps {
  type: AUTOMATION_ACTION_TYPE;
  actionData: AutomationActionActionDataProps;
  userAccessToken: string;
  userId: number;
  shared?: boolean;
  automationHasParent?: boolean;
  onChange: (actionData: AutomationActionActionDataProps) => void;
  onSetupIntegrationService?: (type: INTEGRATION_TYPE) => void;
  onSetupTemplate?: () => void;
  refreshKey?: number;
}

export const AutomationActionFormAction = (
  props: AutomationActionFormActionProps
): React.JSX.Element => {
  const [type, setType] = React.useState<AUTOMATION_ACTION_TYPE>(props.type);
  const [actionData, setActionData] = React.useState<AutomationActionActionDataProps>(
    props.actionData
  );

  const [userStaff, setUserStaff] = React.useState<any>([]);
  const [userStaffLoading, setUserStaffLoading] = React.useState<boolean>(false);

  const [userActivitiesCategories, setUserActivitiesCategories] = React.useState<any>([]);
  const [userActivitiesCategoriesLoading, setUserActivitiesCategoriesLoading] =
    React.useState<boolean>(false);

  const [userLeadStatusNames, setUserLeadStatusNames] = React.useState<any>([]);
  const [userLeadsStatusNamesLoading, setUserLeadsStatusNamesLoading] =
    React.useState<boolean>(false);

  const [userFlows, setUserFlows] = React.useState<Array<UserFlow>>([]);
  const [userFlowNames, setUserFlowNames] = React.useState<any>([]);
  const [userFlowNamesLoading, setUserFlowNamesLoading] = React.useState<boolean>(false);

  const [userFlowStageNames, setUserFlowStageNames] = React.useState<any>([]);
  const [userFlowStageNamesLoading, setUserFlowStageNamesLoading] = React.useState<boolean>(false);

  const webAPI = new WebAPI();
  webAPI.setAccessToken(props.userAccessToken);

  const staffRepository = new StaffRepository();
  staffRepository.setAccessToken(props.userAccessToken);

  const userActivityRepository = new UserActivityRepository();
  userActivityRepository.setAccessToken(props.userAccessToken);

  const userLeadStatusRepository = new UserLeadStatusRepository();
  userLeadStatusRepository.setAccessToken(props.userAccessToken);

  const userFlowRepository = new UserFlowRepository();
  userFlowRepository.setAccessToken(props.userAccessToken);

  const userRepository = new UserRepository();
  userRepository.setAccessToken(props.userAccessToken);

  const handleUserServiceChange = (service: App | UserIntegrationProvider) => {
    const newActionData = { ...actionData };

    newActionData['appId'] = null;
    newActionData['userIntegrationProviderId'] = null;

    if (service !== null) {
      if (service instanceof App) {
        newActionData['appId'] = service.getId();
      } else if (service instanceof UserIntegrationProvider) {
        newActionData['userIntegrationProviderId'] = service.getId();
      }
    }

    setActionData(newActionData);

    if (props.onChange) {
      props.onChange(newActionData);
    }
  };

  const handleActionDataChange = (name, value) => {
    const newActionData = { ...actionData };

    if (value === undefined) {
      delete newActionData[name];
    } else {
      newActionData[name] = value;
    }

    setActionData(newActionData);

    if (props.onChange) {
      props.onChange(newActionData);
    }
  };

  const onActionDataChange = (event: React.SyntheticEvent) => {
    const inputElement = event.target as HTMLFormElement;

    const name = inputElement.name;
    let value;

    if (inputElement.multiple === true) {
      const newArray = [];
      for (const option of inputElement.options) {
        if (option.selected) {
          newArray.push(option.value);
        }
      }
      value = newArray;
    } else if (inputElement.value === '') {
      value = undefined;
    } else if (!isNaN(inputElement.value)) {
      value = parseInt(inputElement.value);
    } else {
      value = inputElement.value;
    }

    handleActionDataChange(name, value);

    if (name == 'userFlow' && value) {
      const thisFlow = userFlows.find((item) => item.getId() === value);
      const stages = thisFlow.getFlowStages().getItems();
      const newList = Array.from(stages, (item): SelectMap => {
        return { value: item.getId(), label: item.getName() };
      });

      setUserFlowStageNames(newList);
      setUserFlowStageNamesLoading(false);
    }
  };

  const fetchUserFlows = async (uId: number, criteria: object = {}) => {
    const res = await userFlowRepository.getUserFlows(uId, {
      include: ['flowStages', 'user'],
      criteria: criteria,
    });
    return res?.getItems?.() ?? [];
  };

  const fetchFlowData = async (targetUser: number) => {
    setUserFlowNamesLoading(true);
    try {
      // get scope first
      const scope = await webAPI.getUserScope(targetUser);
      const isCommunity = String(scope?.userSubTypeDesignation || '').toLowerCase() === 'community';
      const ownerId = scope?.ownerId;

      const promises: Promise<any[]>[] = [fetchUserFlows(targetUser)];
      if (isCommunity && ownerId && ownerId !== targetUser) {
        const criteria = { loaAccess: true };
        promises.push(fetchUserFlows(ownerId, criteria));
      }

      const [selfFlows, ownerFlows = []] = await Promise.all(promises);

      // merge, keeping user's own flows first in order
      const merged = [...(selfFlows || []), ...(ownerFlows || [])];
      setUserFlows(merged);
      const newList = Array.from(merged, (item): SelectMap => {
        return { value: item.getId(), label: item.getName() };
      });

      setUserFlowNames(newList);
      setUserFlowNamesLoading(false);

      // set the stages for selected flow if any
      if (actionData?.userFlowStage && actionData?.userFlow) {
        const selectedFlow = merged.find((item) => item.getId() === actionData.userFlow);
        const stages: UserFlowStage[] = selectedFlow?.getFlowStages().getItems();
        const newList = Array.from(stages, (item): SelectMap => {
          return { value: item.getId(), label: item.getName() };
        });
        setUserFlowStageNames(newList);
      }
    } catch (e) {
      console.error('Failed to load flows', e);
      setUserFlowNames([]);
    } finally {
      setUserFlowNamesLoading(false);
    }
  };

  React.useEffect(() => {
    setType(props.type);
  }, [props.type]);

  React.useEffect(() => {
    setActionData(props.actionData);
  }, [props.actionData]);

  React.useEffect(() => {
    if (type) {
      if (
        type === AUTOMATION_ACTION_TYPE.SEND_EMAIL ||
        type === AUTOMATION_ACTION_TYPE.SEND_MAIL ||
        type === AUTOMATION_ACTION_TYPE.SEND_SMS
      ) {
      } else if (type === AUTOMATION_ACTION_TYPE.CREATE_ACTIVITY) {
        setUserStaffLoading(true);
        staffRepository.getUserStaff(props.userId).then((userStaffResponse) => {
          const newList = Array.from(userStaffResponse.getItems(), (item): SelectMapNumeric => {
            return {
              value: item.getStaffId(),
              label: item.getStaffFirstname() + ' ' + item.getStaffLastname(),
            };
          });

          setUserStaff(newList);
          setUserStaffLoading(false);
        });

        setUserActivitiesCategoriesLoading(true);
        userActivityRepository
          .getUserActivitiesCategories(props.userId)
          .then((userActivitiesCategories) => {
            const newList = Array.from(userActivitiesCategories, (item): SelectMap => {
              return { value: item.name, label: item.name };
            });

            setUserActivitiesCategories(newList);
            setUserActivitiesCategoriesLoading(false);
          });
      } else if (type === AUTOMATION_ACTION_TYPE.UPDATE_LEAD) {
        setUserLeadsStatusNamesLoading(true);
        userLeadStatusRepository
          .getUserLeadsStatusesDeconstructed(props.userId)
          .then((userLeadStatusNames) => {
            const newList = Array.from(userLeadStatusNames, (item): SelectMap => {
              return { value: item.label, label: item.label };
            });

            setUserLeadStatusNames(newList);
            setUserLeadsStatusNamesLoading(false);
          });
      } else if (type === AUTOMATION_ACTION_TYPE.FLOW_UPSERT) {
        fetchFlowData(props.userId);
      }
    }
  }, [type]);

  return (
    <div className="row">
      {type === AUTOMATION_ACTION_TYPE.CREATE_ACTIVITY && (
        <>
          <div className="mb-3 col-4">
            <label className="label mb-0">Who is this activity assigned to?</label>
            {userStaffLoading === true && (
              <div className="mt-2 alert small alert-info" role="alert">
                <i className="fas fa-spinner fa-spin ml-2"></i> Loading....
              </div>
            )}
            {userStaff && userStaffLoading === false && (
              <>
                <div className="form-check">
                  <input
                    onChange={onActionDataChange}
                    value={0}
                    className="form-check-input"
                    type="radio"
                    name="userStaffEnabled"
                    id="userStaffEnabledYes"
                    checked={actionData && actionData.userStaffEnabled !== 1}
                  />
                  <label className="form-check-label small" htmlFor="userStaffEnabledYes">
                    Me
                  </label>
                </div>
                {userStaff.length > 0 && (
                  <>
                    <div className="form-check">
                      <input
                        onChange={onActionDataChange}
                        value={1}
                        className="form-check-input"
                        type="radio"
                        name="userStaffEnabled"
                        id="userStaffEnabledNo"
                        checked={actionData && actionData.userStaffEnabled === 1}
                      />
                      <label className="form-check-label small" htmlFor="userStaffEnabledNo">
                        A member of my team
                      </label>
                    </div>
                    {actionData && actionData.userStaffEnabled === 1 && (
                      <div className="input-group input-group-sm">
                        <select
                          name="userStaff"
                          onChange={onActionDataChange}
                          className="form-control form-control-sm"
                        >
                          <option></option>
                          {userStaff.map((item) => {
                            const selected = actionData && actionData.userStaff === item.value;
                            return (
                              <option selected={selected} value={item.value}>
                                {item.label}
                              </option>
                            );
                          })}
                        </select>
                      </div>
                    )}
                  </>
                )}
              </>
            )}
          </div>

          <div className="mb-3 col-4">
            <label className="label">Activity Category?</label>
            {userActivitiesCategoriesLoading === true && (
              <div className="alert small alert-info" role="alert">
                <i className="fas fa-spinner fa-spin ml-2"></i> Loading....
              </div>
            )}
            {userActivitiesCategoriesLoading === false &&
              userActivitiesCategories &&
              userActivitiesCategories.length === 0 && (
                <div className="alert small alert-info" role="alert">
                  No categories to show
                </div>
              )}
            {userActivitiesCategories && userActivitiesCategories.length > 0 && (
              <div className="input-group input-group-sm">
                <select
                  name="userActivityCategory"
                  onChange={onActionDataChange}
                  className="form-control form-control-sm"
                >
                  <option></option>
                  {userActivitiesCategories.map((item) => {
                    const selected = actionData && actionData.userActivityCategory === item.value;
                    return (
                      <option selected={selected} value={item.value}>
                        {item.label}
                      </option>
                    );
                  })}
                </select>
              </div>
            )}
          </div>

          <div className="mb-3 col-4">
            <label className="label">Activity Priority</label>
            <div className="input-group input-group-sm">
              <select
                name="userActivityPriority"
                onChange={onActionDataChange}
                className="form-control form-control-sm"
              >
                <option></option>
                {USER_ACTIVITY_PRIORITY_TYPE_LABELS.map((item) => {
                  const selected = actionData && actionData.userActivityPriority === item.value;
                  return (
                    <option selected={selected} value={item.value}>
                      {item.label}
                    </option>
                  );
                })}
              </select>
            </div>
          </div>

          <div className="mb-3 col-4">
            <label className="label">Activity Title (20 character max)</label>
            <div className="input-group input-group-sm">
              <input
                value={actionData.userActivityTitle || ''}
                onChange={onActionDataChange}
                type="text"
                className="form-control form-control-sm"
                placeholder="Call John Doe"
                name="userActivityTitle"
                maxLength={20}
              />
            </div>
          </div>

          <div className="mb-3 col-8">
            <label className="label">Activity Details</label>
            <div className="input-group input-group-sm">
              <input
                value={actionData.userActivityDetails || ''}
                onChange={onActionDataChange}
                type="text"
                className="form-control form-control-sm"
                name="userActivityDetails"
              />
            </div>
          </div>
        </>
      )}
      {type === AUTOMATION_ACTION_TYPE.UPDATE_LEAD && (
        <>
          <div className="mb-3 col-4">
            <label className="label">Lead Status</label>
            {userLeadsStatusNamesLoading === true && (
              <div className="alert small alert-info" role="alert">
                <i className="fas fa-spinner fa-spin ml-2"></i> Loading....
              </div>
            )}
            {userLeadsStatusNamesLoading === false &&
              userLeadStatusNames &&
              userLeadStatusNames.length === 0 && (
                <div className="alert small alert-info" role="alert">
                  No lead status options to show
                </div>
              )}
            {userLeadStatusNames && userLeadStatusNames.length > 0 && (
              <div className="input-group input-group-sm">
                <select
                  name="userLeadStatusName"
                  onChange={onActionDataChange}
                  className="form-control form-control-sm"
                >
                  <option></option>
                  {userLeadStatusNames.map((item) => {
                    const selected = actionData && actionData.userLeadStatusName === item.value;
                    return (
                      <option selected={selected} value={item.value}>
                        {item.label}
                      </option>
                    );
                  })}
                </select>
              </div>
            )}
          </div>
        </>
      )}
      {type === AUTOMATION_ACTION_TYPE.FLOW_UPSERT && (
        <>
          <div className="mb-3 col-4">
            <label className="label">Select Flow</label>
            {userFlowNamesLoading === true && (
              <div className="alert small alert-info" role="alert">
                <i className="fas fa-spinner fa-spin ml-2"></i> Loading Flows...
              </div>
            )}
            {userFlowNamesLoading === false && userFlowNames && userFlowNames.length === 0 && (
              <div className="alert small alert-info" role="alert">
                No Flows
              </div>
            )}
            {userFlowNames && userFlowNames.length > 0 && (
              <div className="input-group input-group-sm">
                <select
                  name="userFlow"
                  onChange={onActionDataChange}
                  className="form-control form-control-sm"
                >
                  <option></option>
                  {userFlowNames.map((item) => {
                    const selected = actionData && actionData.userFlow === item.value;
                    return (
                      <option selected={selected} value={item.value}>
                        {item.label}
                      </option>
                    );
                  })}
                </select>
              </div>
            )}
          </div>

          <div className="mb-3 col-4">
            <label className="label">Select Stage</label>
            {userFlowStageNamesLoading === true && (
              <div className="alert small alert-info" role="alert">
                <i className="fas fa-spinner fa-spin ml-2"></i> Loading Stages...
              </div>
            )}
            {userFlowStageNamesLoading === false &&
              userFlowStageNames &&
              userFlowStageNames.length === 0 && (
                <div className="alert small alert-info" role="alert">
                  No stages for selected flow
                </div>
              )}
            {userFlowStageNames && userFlowStageNames.length > 0 && (
              <div className="input-group input-group-sm">
                <select
                  name="userFlowStage"
                  onChange={onActionDataChange}
                  className="form-control form-control-sm"
                >
                  <option></option>
                  {userFlowStageNames.map((item) => {
                    const selected = actionData && actionData.userFlowStage == item.value;
                    return (
                      <option selected={selected} value={item.value}>
                        {item.label}
                      </option>
                    );
                  })}
                </select>
              </div>
            )}
          </div>
        </>
      )}
      {
        <UserServiceSelect
          userId={props.userId}
          userAccessToken={props.userAccessToken}
          type={type}
          templates={true}
          extensions={true}
          phoneNumbers={true}
          appId={actionData ? actionData.appId : undefined}
          userIntegrationProviderId={actionData ? actionData.userIntegrationProviderId : undefined}
          userVoIPExtensionId={actionData ? actionData.userVoIPExtensionId : undefined}
          userVoIPPhoneNumberId={actionData ? actionData.userVoIPPhoneNumberId : undefined}
          userLetterTemplateId={actionData ? actionData.userLetterTemplateId : undefined}
          userContactSyncListId={actionData ? actionData.userContactSyncListId : undefined}
          userContactSyncExtensionId={
            actionData ? actionData.userContactSyncExtensionId : undefined
          }
          userContactSyncAccountId={actionData ? actionData.userContactSyncAccountId : undefined}
          userContactSyncGroupId={actionData ? actionData.userContactSyncGroupId : undefined}
          userAudioFileId={actionData ? actionData.userAudioFileId : undefined}
          onUserVoIPExtensionIdChange={(userVoIPExtensionId) =>
            handleActionDataChange('userVoIPExtensionId', userVoIPExtensionId)
          }
          onUserVoIPPhoneNumberIdChange={(userVoIPPhoneNumberId) =>
            handleActionDataChange('userVoIPPhoneNumberId', userVoIPPhoneNumberId)
          }
          onUserLetterTemplateIdChange={(userLetterTemplateId) =>
            handleActionDataChange('userLetterTemplateId', userLetterTemplateId)
          }
          onUserContactSyncListIdChange={(userContactSyncListId) =>
            handleActionDataChange('userContactSyncListId', userContactSyncListId)
          }
          onUserContactSyncExtensionIdChange={(userContactSyncExtensionId) =>
            handleActionDataChange('userContactSyncExtensionId', userContactSyncExtensionId)
          }
          onUserContactSyncAccountIdChange={(userContactSyncAccountId) =>
            handleActionDataChange('userContactSyncAccountId', userContactSyncAccountId)
          }
          onUserContactSyncGroupIdChange={(userContactSyncGroupId) =>
            handleActionDataChange('userContactSyncGroupId', userContactSyncGroupId)
          }
          onUserAudioFileIdChange={(userAudioFileId) =>
            handleActionDataChange('userAudioFileId', userAudioFileId)
          }
          thanksioTemplate={actionData ? actionData.thanksioTemplate : undefined}
          directMailManagerTemplate={actionData ? actionData.directMailManagerTemplate : undefined}
          onThanksioTemplateChange={(thanksioTemplate) =>
            handleActionDataChange('thanksioTemplate', thanksioTemplate)
          }
          onDirectMailManagerTemplateChange={(directMailManagerTemplate) =>
            handleActionDataChange('directMailManagerTemplate', directMailManagerTemplate)
          }
          onServiceChange={(service) => handleUserServiceChange(service)}
          onSetupIntegrationService={props.onSetupIntegrationService}
          onSetupTemplate={props.onSetupTemplate}
          refreshKey={props.refreshKey}
        />
      }
    </div>
  );
};
